/**
 * @file App routing, detail navigation, and view visibility coordination.
 */

// --- Imports ---

// State management
import { state, appData, appDataDetails, clearTempConfigData, clearTempUsersData, setTempConfigData, clearBoardRuntimeState } from '../state/state.js';
// Firebase services & functions
import { db } from '../firebase/firebase.js';
import { doc, getDoc } from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";
// Utility functions
import {
    getItemType,
    getCollectionName,
    findItem,
} from "../../shared/utils/itemLookup.js";
import { getParentDetailAdapter } from "../detail/parentDetailAdapterRegistry.js";
import { buildDetailPath } from './detailRoutes.js';
import {
    buildViewPath,
    normalizeRouteSubView,
} from './boardRoutes.js';
import {
    getViewAccessDenialMessage,
} from './boardAccess.js';
import {
    getBoardRegistryEntryByItemType,
} from '../../shared/board/boardRegistry.js';
import {
    attachParentDetailListeners,
    attachTaskDetailListeners,
    detachDetailViewListeners,
} from '../data/detailListeners.js';
import { detachRecallsRouteListeners } from '../../domains/recalls/recallsDataListeners.js';
import { detachCalendarRouteListeners } from '../../domains/calendar/calendarDataListeners.js';
import {
    detachAllOrderingListeners,
} from '../../domains/ordering/orderingListeners.js';
import { deactivateOrderingView } from '../../domains/ordering/orderingView.js';
import { requestOrderingModalClose } from '../../domains/ordering/orderingModal.js';
import { resetOrderingRouteState } from '../../domains/ordering/orderingState.js';
import { discardOrderingSettingsDraft } from '../../domains/settings/orderingSettings.js';
import { deactivateComplianceView } from '../../domains/compliance/complianceView.js';
import { requestComplianceModalClose } from '../../domains/compliance/complianceModals.js';
import {
    discardComplianceSettingsDraft,
} from '../../domains/settings/complianceSettings.js';
import { saveDetailsForm } from '../../shared/api/details.js';
import { saveNoteContent } from '../../shared/api/notes.js';
import { renderCurrentBoard } from '../view/viewController.js';
import { renderDetailsView } from '../detail/detailController.js';
import { openConfirmationModal } from '../../shared/ui/confirmationModal.js';
import {
    openDetailLeaveModal,
    savePendingDetailDrafts,
} from '../../shared/details/detailLeaveGuard.js';
import { resetChecklistUiSession } from '../../shared/details/checklistSection.js';
import { resetLinkedTaskUiSession } from '../../shared/details/linkedItemsSection.js';

function saveCurrentDetailDraftsAndThen(callback) {
    return async () => {
        const didSave = await savePendingDetailDrafts({
            root: document,
            saveDetailsForm: saveActiveDetailForm,
            saveNoteContent,
        });
        if (didSave) {
            await callback();
        }
    };
}

function confirmPendingDetailLeave(callback, onStay = null) {
    return openDetailLeaveModal({
        onContinue: callback,
        onSave: saveCurrentDetailDraftsAndThen(callback),
        onStay,
    });
}

function getActiveDetailItemAndType() {
    const itemId = state.selectedItemId;
    let item = findItem(itemId);
    let itemType = state.selectedItemType || getItemType(item?.id);
    if (!item && itemId) {
        item = appDataDetails.tasks.find((task) => task.id === itemId);
        if (item) {itemType = "task";}
    }
    return { item, itemType };
}

async function saveActiveDetailForm(detailsForm) {
    const { item, itemType } = getActiveDetailItemAndType();
    const detailAdapter = getParentDetailAdapter(itemType);
    if (detailAdapter?.saveManualDetails) {
        return detailAdapter.saveManualDetails({
            detailsForm,
            item,
            itemType,
        });
    }
    return saveDetailsForm(detailsForm);
}

// --- View Management & Navigation ---

/**
 * Navigates to a specific main view (e.g., 'tasks', 'patients', 'settings').
 * Checks for unsaved changes before proceeding.
 * @param {string} viewName - The key of the view to navigate to.
 * @param {string|null} subView - Optional sub-view state (e.g., for drugs).
 * @param {object} options - Optional navigation settings.
 * @param {Function} [options.onNavigationComplete] Callback after a route is applied.
 * @param {Function} [options.onNavigationCancelled] Callback when a leave prompt is declined.
 */
export function navigate(viewName, subView = null, options = {}) {
    if (!options.skipOrderingModalGuard && requestOrderingModalClose({
        onClose: () => navigate(viewName, subView, {
            ...options,
            skipOrderingModalGuard: true,
        }),
        onStay: () => {
            if (options.historyMode === "none") {
                const currentSubView = state.currentView === "drugs" ? state.drugsView :
                    state.currentView === "calendar" ? state.calendarView : null;
                history.replaceState({
                    view: state.currentView,
                    subView: currentSubView,
                }, "", buildViewPath(state.currentView, currentSubView));
            }
            options.onNavigationCancelled?.();
        },
    })) {
        return;
    }
    if (!options.skipComplianceModalGuard && requestComplianceModalClose({
        onClose: () => navigate(viewName, subView, {
            ...options,
            skipComplianceModalGuard: true,
        }),
        onStay: () => {
            restoreCurrentRouteAfterCancelledNavigation(options);
            options.onNavigationCancelled?.();
        },
    })) {
        return;
    }
    if (
        state.selectedItemId &&
        confirmPendingDetailLeave(
            () => completeNavigation(viewName, subView, options),
            options.onNavigationCancelled,
        )
    ) {
        return;
    }

    // 1. Check for unsaved changes global flag
    if (state.hasUnsavedChanges) {
        openConfirmationModal(
            'Unsaved Changes',
            'You have unsaved changes. Are you sure you want to leave? Your changes will be lost.',
            () => {
                // User confirmed they want to leave
                state.hasUnsavedChanges = false; // Reset flag
                completeNavigation(viewName, subView, options); // Proceed with navigation
            },
            true,
            "Continue",
            options.onNavigationCancelled,
        );
        return; // Stop execution until confirmed
    }

    // 2. No changes, proceed immediately
    completeNavigation(viewName, subView, options);
}

function restoreCurrentRouteAfterCancelledNavigation(options) {
    if (options.historyMode !== "none") {return;}
    const currentSubView = state.currentView === "drugs" ? state.drugsView :
        state.currentView === "calendar" ? state.calendarView : null;
    history.replaceState({
        view: state.currentView,
        subView: currentSubView,
    }, "", buildViewPath(state.currentView, currentSubView));
}

/**
 * Resolves a navigation request only after the existing leave guards accept it.
 * @param {string} viewName The requested main view.
 * @param {string|null} [subView=null] Optional requested sub-view.
 * @param {object} [options={}] Existing navigation options.
 * @returns {Promise<boolean>} Whether the requested route was applied.
 */
export function navigateWhenConfirmed(viewName, subView = null, options = {}) {
    return new Promise((resolve) => {
        navigate(viewName, subView, {
            ...options,
            onNavigationComplete: () => {
                options.onNavigationComplete?.();
                resolve(true);
            },
            onNavigationCancelled: () => {
                options.onNavigationCancelled?.();
                resolve(false);
            },
        });
    });
}

function completeNavigation(viewName, subView, options) {
    const applied = forceNavigate(viewName, subView, options);
    if (applied) {
        options.onNavigationComplete?.();
    } else {
        options.onNavigationCancelled?.();
    }
}

/**
 * Navigates from the current detail record to another detail record.
 * @param {string} id - Target item id.
 * @param {string|null} providedType - Optional target item type.
 */
export function navigateToDetails(id, providedType = null) {
    if (
        state.selectedItemId &&
        state.selectedItemId !== id &&
        confirmPendingDetailLeave(() => showDetails(id, providedType, {
            skipUnsavedDetailCheck: true,
        }))
    ) {
        return;
    }

    if (state.hasUnsavedChanges) {
        openConfirmationModal(
            'Unsaved Changes',
            'You have unsaved changes. Are you sure you want to leave? Your changes will be lost.',
            () => {
                state.hasUnsavedChanges = false;
                showDetails(id, providedType);
            }
        );
        return;
    }

    showDetails(id, providedType);
}

function upsertCachedItem(collectionName, item) {
    if (!item?.id || !Array.isArray(appData[collectionName])) {return;}
    const index = appData[collectionName].findIndex((entry) => entry.id === item.id);
    if (index === -1) {
        appData[collectionName].push(item);
    } else {
        appData[collectionName][index] = item;
    }
}

async function fetchItemForDetails(id, itemType) {
    if (!id || !itemType) {return null;}
    const collectionName = getCollectionName(itemType);
    if (!collectionName) {return null;}

    try {
        const snapshot = await getDoc(doc(db, collectionName, id));
        if (!snapshot.exists()) {return null;}
        const item = { id: snapshot.id, ...snapshot.data() };
        upsertCachedItem(collectionName, item);
        return item;
    } catch (error) {
        console.error(`Failed to fetch ${itemType} ${id} for details view:`, error);
        return null;
    }
}

/**
 * Helper: Executes the actual navigation logic (Permission checks, State updates, History).
 * This contains the logic previously inside 'navigate'.
 */
function applyHistoryState(stateValue, urlPath, historyMode = 'push') {
    if (historyMode === 'none') {return;}
    if (historyMode === 'replace') {
        history.replaceState(stateValue, '', urlPath);
        return;
    }
    history.pushState(stateValue, '', urlPath);
}

function forceNavigate(viewName, subView = null, options = {}) {
    const accessDenialMessage = getViewAccessDenialMessage(
        state.currentUserProfile,
        viewName,
    );
    if (accessDenialMessage) {
        console.warn(accessDenialMessage);
        return false;
    }

    if (state.currentView === "settings" && viewName !== "settings") {
        discardOrderingSettingsDraft();
        discardComplianceSettingsDraft();
    }

    // Reset WIP state unless returning directly from details to origin
    const comingFromDetails = state.selectedItemId !== null;
    const returningToOrigin = viewName === state.originatingView;
    if (!(comingFromDetails && returningToOrigin)) {
        state.showWIP = false;
        state.showClosed = false;
    }

    if (viewName !== state.currentView) {
        if (state.currentView === "recalls") {
            detachRecallsRouteListeners();
        }
        if (state.currentView === "calendar") {
            detachCalendarRouteListeners();
        }
        if (state.currentView === "ordering") {
            detachAllOrderingListeners();
            deactivateOrderingView();
            resetOrderingRouteState();
        }
        if (state.currentView === "compliance") {
            deactivateComplianceView();
        }
        clearBoardRuntimeState();
    }

    // Detach listeners specific to the previous details view, if any
    detachDetailViewListeners();
    resetChecklistUiSession();
    resetLinkedTaskUiSession();

    // Update state for the new view
    state.currentView = viewName;
    state.selectedItemId = null; // Clear selected item when navigating to a main view
    state.selectedItemType = null;
    state.detailNavigationStack = [];
    state.originatingView = viewName; // Set originating view for potential future detail navigation

    const normalizedSubView = normalizeRouteSubView(viewName, subView);
    if (viewName === 'drugs') {
        state.drugsView = normalizedSubView;
    }
    if (viewName === 'calendar') {
        state.calendarView = normalizedSubView;
    } else {
        state.calendarView = null;
    }

    const urlPath = buildViewPath(viewName, normalizedSubView);
    applyHistoryState({
        view: viewName,
        subView: normalizedSubView,
    }, urlPath, options.historyMode);

    // Handle temporary settings data
    if (viewName === 'settings') {
        setTempConfigData(appData.config); // Use the imported function
        // Note: tempUsersData is set in settings.js when rendering users tab
    } else {
        // Clear temp data when navigating away from settings
        clearTempConfigData();
        clearTempUsersData();
    }

    // Update the UI to show the correct view
    updateView();
    return true;
}

/**
 * Navigates to the details view for a specific item.
 * Sets the selected item ID and triggers a view update.
 * Attaches specific listeners if the item is a parent type (patient, project, asset).
 * @param {string} id - The ID of the item to show details for.
 * @param {string|null} providedType - Optional item type.
 * @param {object} options - Optional navigation settings.
 */
export async function showDetails(id, providedType = null, options = {}) {
    if (!id) {
        console.error("showDetails called with invalid ID.");
        return;
    }
    if (!options.skipComplianceModalGuard && requestComplianceModalClose({
        onClose: () => showDetails(id, providedType, {
            ...options,
            skipComplianceModalGuard: true,
        }),
        onStay: () => restoreCurrentRouteAfterCancelledNavigation(options),
    })) {
        return;
    }

    if (
        !options.skipUnsavedDetailCheck &&
        state.selectedItemId &&
        state.selectedItemId !== id &&
        confirmPendingDetailLeave(() => showDetails(id, providedType, {
            ...options,
            skipUnsavedDetailCheck: true,
        }))
    ) {
        return;
    }

    // Clear temp settings data if navigating away from settings view
    if (state.currentView === 'settings') {
        discardOrderingSettingsDraft();
        discardComplianceSettingsDraft();
        clearTempConfigData();
        clearTempUsersData();
    }

    const previousDetailId = state.selectedItemId;
    const previousDetailType = state.selectedItemType ||
        (previousDetailId ? getItemType(previousDetailId) : null);
    const comingFromDetails = previousDetailId !== null;

    if (comingFromDetails && !options.fromBack && previousDetailId !== id) {
        state.detailNavigationStack.push({
            id: previousDetailId,
            type: previousDetailType,
        });
    } else if (!comingFromDetails) {
        state.detailNavigationStack = [];
        state.originatingView = state.currentView;
    }

    if (!comingFromDetails) {
        state.originatingView = state.currentView;
    }
    let itemType = providedType;

    if (!itemType) {
        itemType = getItemType(id);
        if (!itemType) {
            const taskInDetails = appDataDetails.tasks.find(t => t.id === id);
            if (taskInDetails) {
                itemType = 'task';
            } else if (appData.tasks.find(t => t.id === id)) {
                itemType = 'task';
            }
        }
    }

    if (!itemType && providedType) {
        itemType = providedType;
    }

    const detailBoard = getBoardRegistryEntryByItemType(itemType);
    const isParentBoardDetail = Boolean(detailBoard) &&
        detailBoard.collectionName !== 'tasks';

    if (isParentBoardDetail) {
        state.currentView = detailBoard.viewKey;
    } else if (itemType === 'task') {
        state.currentView = 'tasks';
    }

    state.selectedItemId = id;
    state.selectedItemType = itemType || null;

    const detailPath = buildDetailPath(itemType, id);
    applyHistoryState(
        { view: 'details', itemId: id, itemType: itemType || null },
        detailPath || window.location.pathname,
        options.historyMode,
    );

    const missingFromCache = !findLoadedItemForDetails(id, itemType);
    if (missingFromCache && itemType) {
        await fetchItemForDetails(id, itemType);
    }

    if (isParentBoardDetail) {
        // We're viewing a parent. Detach all and attach new listeners.
        attachParentDetailListeners(id, itemType);
    } else if (itemType === 'task') {
        // We're viewing a task. ONLY swap the notes listener.
        attachTaskDetailListeners(id);
    } else {
        console.error(`showDetails: Could not determine itemType for ${id}.`);
    }
    updateView();
}

function findLoadedItemForDetails(id, itemType) {
    if (!id) {return null;}
    if (itemType === 'task') {
        return appData.tasks.find((task) => task.id === id) ||
            appDataDetails.tasks.find((task) => task.id === id);
    }

    const collectionName = itemType ? getCollectionName(itemType) : null;
    if (!collectionName || !Array.isArray(appData[collectionName])) {return null;}
    return appData[collectionName].find((item) => item.id === id) || null;
}

/**
 * Updates the visibility of view containers based on the current state
 * (`state.currentView` and `state.selectedItemId`). Calls the appropriate
 * rendering function for the active view. (Internal helper function).
 */
export function updateView() {
    // Hide all main view containers first
    document.querySelectorAll('.view').forEach(v => v.classList.add('hidden'));
    // Update active state on navigation links
    document.querySelectorAll('.nav-link').forEach(l => {
        l.classList.toggle('active', l.dataset.view === state.currentView);
    });
    document.querySelectorAll('[data-mobile-view]').forEach(l => {
        const isActive = l.dataset.mobileView === state.currentView;
        l.classList.toggle('active', isActive);
        l.setAttribute('aria-current', isActive ? 'page' : 'false');
    });

    // Update active state for settings button
    const settingsBtn = document.getElementById('settings-btn');
    if (settingsBtn) {
        settingsBtn.classList.toggle('active', state.currentView === 'settings');
    }
    const mobileSettingsBtn = document.getElementById('mobile-settings-btn');
    if (mobileSettingsBtn) {
        const isActive = state.currentView === 'settings';
        mobileSettingsBtn.classList.toggle('active', isActive);
        mobileSettingsBtn.setAttribute('aria-current', isActive ? 'page' : 'false');
    }
    document.querySelectorAll('#calendar-btn, #mobile-calendar-btn').forEach((button) => {
        const isActive = state.currentView === 'calendar';
        button.classList.toggle('active', isActive);
        button.setAttribute('aria-current', isActive ? 'page' : 'false');
    });
    const mobileCalendarMenuBtn = document.getElementById('mobile-calendar-menu-btn');
    if (mobileCalendarMenuBtn) {
        const isActive = state.currentView === 'calendar';
        mobileCalendarMenuBtn.classList.toggle('active', isActive);
        mobileCalendarMenuBtn.setAttribute('aria-current', isActive ? 'page' : 'false');
    }

    if (state.selectedItemId) {
        // Show details view and render its content
        const detailsViewEl = document.getElementById('details-view');
        if (detailsViewEl) {
            detailsViewEl.classList.remove('hidden');
            renderDetailsView(); // Assumed in renderDetails.js
        } else {
            console.error("Details view container (#details-view) not found.");
        }
    } else {
        // Show the active main view container and render its content
        const activeViewEl = document.getElementById(`${state.currentView}-view`);
        if (activeViewEl) {
            activeViewEl.classList.remove('hidden');
            renderCurrentBoard();
        } else {
            console.error(`View container (#${state.currentView}-view) not found.`);
            // Fallback? Navigate to tasks?
            navigate('tasks');
        }
    }
}
